import { serverTimestamp, Timestamp } from 'firebase/firestore';
import { isRecord, type Doc } from '@day';

/**
 * Stops waiting for a route (docs/waiting-stops.md): `businesses/{id}/waitingStops/{stopId}`, one record a stop,
 * typed in or read from a sheet before its route exists. The stop is DeliveryStop as the phone stores it
 * (`storedStop`), so the phone can read it later; its lat/lng are only ever the customer's own pin. A position from
 * Google sits apart, in `google`, until `googleUntil`, and the stop is looked up again when it's planned after that.
 */

const DAY = 24 * 60 * 60 * 1000;
/** A waiting stop names a customer, so it goes this long after it was added (TTL on `expiresAt`), whatever happens. */
export const KEEP_MS = 28 * DAY;
/** Google's positions are kept 29 days at most (functions/src/google_retention.ts GOOGLE_KEEP_DAYS). */
export const GOOGLE_MS = 29 * DAY;
/** A position from Google this new goes onto a route as it is; an older one is looked up again (the route's 29 days start then). */
export const FRESH_MS = DAY;
/**
 * Written this much short of the limits (the rules allow an hour for a computer's clock), so a clock up to 3 hours fast
 * still saves: the record's 28 days and Google's 29 then end a little early, never late.
 */
export const CLOCK_MARGIN_MS = 3 * 60 * 60 * 1000;
/** When a position Google gave at [at] has to go: [GOOGLE_MS] on, a little short of it ([CLOCK_MARGIN_MS]). */
export const googleUntilOf = (at: number) => at + GOOGLE_MS - CLOCK_MARGIN_MS;
/** The longest address the rules take (firestore.rules `validWaitingStop`). */
export const MAX_ADDRESS = 500;
/** The most couriers a stop can name as the only ones allowed to take it (the rules' limit, as a zone's). */
export const MAX_COURIERS = 50;

/** The last day a waiting stop added now can be for: the day before it goes. */
export const lastDay = (today: string) => {
  const [y, m, d] = today.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + 27)).toISOString().slice(0, 10);
};

/** A deadline with only a time ("14:30"), on the day the stop goes out: stored as a sent route's deliverBy is. */
export const deadlineOn = (day: string, byTime: string | null | undefined) => (byTime && /^\d{2}:\d{2}$/.test(byTime) ? `${day}T${byTime}:00.000` : null);

/**
 * A stable id for a sheet's row, from the file, the row and what's in it: the same sheet read again makes the same ids,
 * so a stop already waiting isn't added twice (a retry after a failed write adds only what's missing).
 */
export function sheetStopId(file: string, row: number, content: string): string {
  const h = (seed: number) => {
    let x = seed;
    for (const ch of `${file}|${row}|${content}`) x = Math.imul(x ^ ch.codePointAt(0)!, 16777619) >>> 0;
    return x.toString(16).padStart(8, '0');
  };
  return `sheet-${h(2166136261)}${h(5381)}`;
}

export type Source = 'typed' | 'sheet';

export interface Waiting {
  id: string;
  /** As stored (`storedStop`): what goes onto a route. */
  stop: Doc;
  address: string;
  /** "7 Yedseram Street": the first part of the address, with the house number. */
  street: string;
  area: string;
  placeId: string | null;
  name: string | null;
  phone: string | null;
  notes: string | null;
  type: 'delivery' | 'pickup';
  packages: number;
  /** What's collected at the door, in minor units, and its currency. */
  collect: number;
  currency: string | null;
  /** The business's day it's for ("2026-11-03"), if one was said. */
  day: string | null;
  /** A deliver-by time with no day ("14:30"): the stop's deliverBy on the day it goes out. */
  byTime: string | null;
  /** The only couriers allowed to take it (their uids); null: anyone. Share out joins it with the zones. */
  couriers: string[] | null;
  source: Source;
  sheet: { file: string; row: number } | null;
  addedAt: number | null;
  expiresAt: number | null;
  /** The customer's own pin: kept as long as the stop. */
  pin: { lat: number; lng: number } | null;
  /** Google's position, while it may be kept. */
  google: { lat: number; lng: number } | null;
  googleUntil: number | null;
}

const ms = (v: unknown): number | null =>
  v instanceof Timestamp ? v.toMillis() : typeof v === 'number' ? v : isRecord(v) && typeof (v as { toMillis?: unknown }).toMillis === 'function' ? (v as unknown as Timestamp).toMillis() : null;
const str = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : null);

export function readWaiting(id: string, d: Doc, now: number): Waiting | null {
  const stop = isRecord(d.stop) ? (d.stop as Doc) : null;
  if (!stop || typeof stop.address !== 'string') return null;
  const address = stop.address;
  const parts = address.split(',').map((x) => x.trim()).filter(Boolean);
  const house = str(stop.houseNumber);
  const first = parts[0] ?? address;
  const until = ms(d.googleUntil);
  const g = isRecord(d.google) && typeof d.google.lat === 'number' && typeof d.google.lng === 'number' && until != null && until > now ? { lat: d.google.lat as number, lng: d.google.lng as number } : null;
  const sheet = isRecord(d.sheet) && typeof d.sheet.file === 'string' ? { file: d.sheet.file as string, row: Number(d.sheet.row) || 0 } : null;
  return {
    id,
    stop,
    address,
    street: house && !first.startsWith(house) ? `${house} ${first}` : first,
    area: parts.slice(1, 3).join(', '),
    placeId: str(stop.placeId),
    name: str(stop.recipientName),
    phone: str(stop.recipientPhone),
    notes: [str(stop.accessInstructions), str(stop.notes)].filter(Boolean).join(' · ') || null,
    type: stop.type === 'pickup' ? 'pickup' : 'delivery',
    packages: typeof stop.packageCount === 'number' ? stop.packageCount : 1,
    collect: (Number(stop.collectOrder) || 0) + (Number(stop.collectFee) || 0),
    currency: str(stop.currency),
    day: typeof d.day === 'string' ? d.day : null,
    byTime: typeof d.byTime === 'string' ? d.byTime : null,
    couriers: couriersOf(d.couriers),
    source: d.source === 'sheet' ? 'sheet' : 'typed',
    sheet,
    addedAt: ms(d.addedAt),
    expiresAt: ms(d.expiresAt),
    pin: typeof stop.lat === 'number' && typeof stop.lng === 'number' ? { lat: stop.lat, lng: stop.lng } : null,
    google: g,
    googleUntil: g ? until : null,
  };
}

/** A stop's allowed couriers as kept: uids, each once; null for anyone. */
export function couriersOf(v: unknown): string[] | null {
  const xs = Array.isArray(v) ? [...new Set(v.filter((x): x is string => typeof x === 'string' && !!x))] : [];
  return xs.length ? xs.slice(0, MAX_COURIERS) : null;
}

/** What's wrong with a waiting stop before it's written, in words, or null: the rules would refuse it. */
export function problemOf(stop: { address?: unknown }, day?: string | null): string | null {
  const a = String(stop.address ?? '');
  if (!a.trim()) return 'it has no address';
  if (a.length > MAX_ADDRESS) return `its address is longer than ${MAX_ADDRESS} characters: shorten it`;
  if (day && !/^\d{4}-\d{2}-\d{2}$/.test(day)) return 'its day isn’t a date';
  return null;
}

/** Where a waiting stop is now, if anywhere: the customer's pin, else Google's while it may be kept. */
export const spotOf = (w: Waiting) => w.pin ?? w.google;

/** Whether it goes onto a route as it is (a pin, or Google's position from the last day), or is looked up again first. */
export const readyToPlan = (w: Waiting, now: number) => !!w.pin || (!!w.google && w.googleUntil != null && w.googleUntil - now > GOOGLE_MS - FRESH_MS);

/** A new waiting stop's record, as the rules take it (firestore.rules `validWaitingStop`). */
export function waitingFields(o: {
  businessId: string;
  stop: Doc & { id: string; address: string };
  source: Source;
  sheet?: { file: string; row: number } | null;
  day?: string | null;
  byTime?: string | null;
  couriers?: string[] | null;
  google?: { lat: number; lng: number; at: number } | null;
  now: number;
}): Doc {
  const couriers = couriersOf(o.couriers);
  return {
    businessId: o.businessId,
    stop: o.stop,
    source: o.source,
    ...(o.source === 'sheet' && o.sheet ? { sheet: { file: o.sheet.file.slice(0, 120), row: Math.round(o.sheet.row) } } : {}),
    ...(o.day ? { day: o.day } : {}),
    ...(o.byTime ? { byTime: o.byTime } : {}),
    ...(couriers ? { couriers } : {}),
    ...(o.google ? { google: { lat: o.google.lat, lng: o.google.lng }, googleUntil: Timestamp.fromMillis(o.google.at + GOOGLE_MS - CLOCK_MARGIN_MS) } : {}),
    addedBy: o.businessId,
    addedAt: serverTimestamp(),
    expiresAt: Timestamp.fromMillis(o.now + KEEP_MS - CLOCK_MARGIN_MS),
  };
}
